import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
  Alert,
  Divider,
  FormHelperText,
  AspectRatio
} from '@mui/joy';
import {
  Sparkle as AIIcon,
  Image as ImageIcon,
  Clock as ScheduleIcon,
  Eye as PreviewIcon,
  FacebookLogo as FacebookIcon,
  InstagramLogo as InstagramIcon,
  Buildings as BusinessIcon,
  Calendar as CalendarIcon,
  Clock as TimeIcon,
  X as CloseIcon,
  ArrowsClockwise as RefreshIcon,
  Star as ReviewIcon,
  Lightbulb as TipsIcon,
  Tag as OfferIcon,
  Megaphone as AnnouncementIcon,
  ArrowLeft as BackIcon,
  Plus as AddIcon,
  Trash as DeleteIcon
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { formatForDatetimeLocalInput, parseFromDatetimeLocalInput, getOrganizationTimezone } from '../../lib/timezoneUtils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { SocialMediaPreview } from './SocialMediaPreview';
import { TestimonialSelector } from './TestimonialSelector';
import { TestimonialTemplateSelector } from './TestimonialTemplateSelector';
import type { Review } from '../../types/marketing';
import { MediaLibrary } from './MediaLibrary';
import { ImageComposer } from './ImageComposer';

interface SocialMediaAccount {
  id: number;
  platform: string;
  platform_display: string;
  account_name: string;
  is_active: boolean;
}

interface SocialMediaPostComposerProps {
  open: boolean;
  onClose: () => void;
  initialDate?: Date;
  editingPost?: any;
}

export const SocialMediaPostComposer: React.FC<SocialMediaPostComposerProps> = ({
  open,
  onClose,
  initialDate,
  editingPost
}) => {
  const queryClient = useQueryClient();
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const lastSession = useRef('');
  const [dirty, setDirty] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string,string>>({});
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState<string>('general');
  const [selectedPlatforms, setSelectedPlatforms] = useState<number[]>([]);
  const [scheduledDate, setScheduledDate] = useState(initialDate ? format(initialDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'));
  const [scheduledTime, setScheduledTime] = useState('10:00');
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [showTestimonialSelector, setShowTestimonialSelector] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState<Review | null>(null);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [showMediaSelector, setShowMediaSelector] = useState(false);
  const [showImageComposer, setShowImageComposer] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<any[]>([]);

  const {data: organization} = useQuery({queryKey:['organization'],queryFn:async()=>
    (await api.get<{name?:string;logo_square_full_color?:string;logo_url?:string;time_zone?:string}>('/api/organizations/current/')).data});
  const orgTimezone = organization?.time_zone || getOrganizationTimezone();
  useEffect(() => {
    if (!open || !dirty) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [open, dirty]);
  const marketingImages = selectedMedia.filter(media=>media.source !== 'production' && !String(media.id).startsWith('production_'));
  const previewImages = (marketingImages.length ? marketingImages : selectedMedia).map(media=>media.file || media.image_url || media.image).filter(Boolean);
  const previewContent = {...(editingPost?.platform_content || {})};
  if (editingPost && content !== editingPost.content) {
    for (const platform of ['facebook','instagram','google_business_profile']) {
      if (previewContent[platform]) { previewContent[platform] = {...previewContent[platform]}; delete previewContent[platform].message; }
    }
  }

  // Fetch accounts
  const { data: accounts } = useQuery({
    queryKey: ['social-accounts'],
    queryFn: async () => {
      const response = await api.getAllPages('/api/marketing/social-accounts/');
      return response.data;
    }
  });

  // Load editing post data
  useEffect(() => {
    if (!open) { lastSession.current = ''; return; }
    const session = `${editingPost?.id || 'new'}:${initialDate?.getTime() || ''}`;
    if (lastSession.current === session && dirty) return;
    lastSession.current = session;
    setDirty(false);
    setFieldErrors({});
    setTitle('');
    setContent('');
    setPostType('general');
    setSelectedPlatforms([]);
    setScheduledDate(format(initialDate || new Date(), 'yyyy-MM-dd'));
    setScheduledTime('10:00');
    setGenerationError(null);
    setPreviewMode(false);
    setSelectedTestimonial(null);
    setSelectedMedia([]);
    setShowTestimonialSelector(false);
    setShowTemplateSelector(false);
    setShowMediaSelector(false);
    setShowImageComposer(false);
    if (editingPost) {
      setTitle(editingPost.title || '');
      setContent(editingPost.content || '');
      // Explicitly handle null/undefined post_type
      setPostType(editingPost.post_type && editingPost.post_type !== null ? editingPost.post_type : 'general');
      setSelectedPlatforms(editingPost.platforms || []);
      if (editingPost.scheduled_time) {
        // Use timezone utility to extract date/time for form fields
        const [date, time] = formatForDatetimeLocalInput(new Date(editingPost.scheduled_time), orgTimezone).split('T');
        setScheduledDate(date);
        setScheduledTime(time);
      }
      
      // The post detail already contains both sources; no paginated library lookup.
      setSelectedMedia([
        ...(editingPost.media_detail || []).map((media: any) => ({
          ...media, id: `marketing_${media.id}`, source: 'marketing',
          file: media.file || media.image_url || media.file_url,
          thumbnail: media.thumbnail || media.thumbnail_url,
        })),
        ...(editingPost.production_photos_detail || []).map((photo: any) => ({
          ...photo, id: `production_${photo.id}`, source: 'production',
          name: photo.name || photo.caption || 'Project photo',
          file: photo.image_url || photo.image || photo.file,
          thumbnail: photo.thumbnail_url || photo.thumbnail,
        })),
      ]);
      setSelectedTestimonial(editingPost.customer_review_detail ||
        (editingPost.customer_review ? {id: editingPost.customer_review} : null));

    }
  }, [open, editingPost, initialDate, orgTimezone, dirty]);

  // Create/Update post mutation
  const createPostMutation = useMutation({
    mutationFn: async (data: any) => {
      if (editingPost) {
        return api.patch(`/api/marketing/social-posts/${editingPost.id}/`, data);
      } else {
        return api.post('/api/marketing/social-posts/', data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-posts'] });
      resetAndClose();
    },
    onError: (error: any) => {
      const details = error.response?.data;
      if (details && typeof details === 'object') setFieldErrors(Object.fromEntries(Object.entries(details).map(([key,value])=>[key,String(value)])));
      setGenerationError(details ? Object.entries(details).map(([field, value]) => `${field}: ${String(value)}`).join(' ') : 'Unable to save the post. Please try again.');
    }
  });

  // Delete post mutation
  const deletePostMutation = useMutation({
    mutationFn: async () => {
      if (!editingPost) return;
      return api.delete(`/api/marketing/social-posts/${editingPost.id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-posts'] });
      resetAndClose();
    },
    onError: (error: any) => {
      console.error('Failed to delete post:', error);
      alert('Failed to delete post. Please try again.');
    }
  });

  // Generate content mutation
  const generateContentMutation = useMutation({
    mutationFn: async (currentMedia: any[]) => {
      // Extract media IDs from current media for vision analysis
      // Note: We pass currentMedia as parameter to avoid stale closure issues
      // IDs may be prefixed like 'marketing_56' or 'production_123' from combined-media endpoint
      const mediaIds = currentMedia.map(m => {
        const idStr = String(m.id);
        if (idStr.startsWith('marketing_')) {
          return { id: parseInt(idStr.replace('marketing_', ''), 10), source: 'marketing' };
        } else if (idStr.startsWith('production_')) {
          return { id: parseInt(idStr.replace('production_', ''), 10), source: 'production' };
        }
        // Fallback: assume it's a plain integer ID (likely MarketingMedia)
        return { id: parseInt(idStr, 10), source: 'marketing' };
      }).filter(item => !isNaN(item.id));

      return api.post('/api/marketing/social-posts/generate-content/', {
        post_type: postType,
        platforms: selectedPlatforms,
        media_ids: mediaIds
      });
    },
    onSuccess: (response) => {
      if (response.data) {
        const responseData = response.data as any;
        setDirty(true);
        setTitle(responseData.title || '');
        setContent(responseData.content || '');
      }
    },
    onError: (error: any) => {
      setGenerationError(error.message || 'Failed to generate content');
    }
  });

  const handleClose = () => {
    if (createPostMutation.isPending || deletePostMutation.isPending) return;
    if (dirty) { setConfirmDiscard(true); return; }
    resetAndClose();
  };
  const resetAndClose = () => {
    setConfirmDiscard(false);
    setDirty(false);
    setFieldErrors({});
    setTitle('');
    setContent('');
    setPostType('general');
    setSelectedPlatforms([]);
    setScheduledDate(format(new Date(), 'yyyy-MM-dd'));
    setScheduledTime('10:00');
    setGenerationError(null);
    setPreviewMode(false);
    setSelectedTestimonial(null);
    setSelectedMedia([]);
    onClose();
  };

  const handleTestimonialSelect = (review: Review) => {
    setDirty(true);
    setSelectedTestimonial(review);
    setPostType('testimonial');
    // Close testimonial selector and open template selector
    setShowTestimonialSelector(false);
    setShowTemplateSelector(true);
  };

  const handleGeneratedImage = (media: any) => {
    setDirty(true);
    // Add the generated image to selected media
    setSelectedMedia(prev => [...prev, media]);
    // Auto-populate content with testimonial text
    if (selectedTestimonial) {
      const testimonialContent = `"${selectedTestimonial.review_text}"\n\n- ${selectedTestimonial.reviewer_name}${selectedTestimonial.rating ? ` ${'⭐'.repeat(selectedTestimonial.rating)}` : ''}`;
      setContent(testimonialContent);
    }
    setShowTemplateSelector(false);
  };

  const handleMediaSelect = (media: any) => {
    setDirty(true);
    setSelectedMedia(prev => [...prev, media]);
    setShowMediaSelector(false);
  };

  const handleRemoveMedia = (mediaId: string | number) => {
    setDirty(true);
    setSelectedMedia(prev => prev.filter(m => m.id !== mediaId));
  };

  const handleDelete = async () => {
    if (!editingPost) return;
    
    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      return;
    }
    
    await deletePostMutation.mutateAsync();
  };

  const handleGenerateContent = async () => {
    if (selectedPlatforms.length === 0) {
      setGenerationError('Please select at least one platform first');
      return;
    }
    
    setIsGeneratingContent(true);
    setGenerationError(null);

    try {
      // Pass selectedMedia as parameter to avoid stale closure
      await generateContentMutation.mutateAsync(selectedMedia);
    } catch (error) {
      console.error('Generation error:', error);
    } finally {
      setIsGeneratingContent(false);
    }
  };

  const handleSubmit = async (nextStatus = editingPost?.status === 'failed' ? 'draft' : editingPost?.status || 'scheduled') => {
    const errors: Record<string,string> = {};
    if (!title.trim()) errors.title = 'Enter a title.';
    if (!content.trim()) errors.content = 'Enter a caption.';
    let scheduledTimeUTC: string | null = editingPost?.scheduled_time || null;
    if (nextStatus !== 'scheduled' && scheduledDate && scheduledTime) {
      try { scheduledTimeUTC = parseFromDatetimeLocalInput(`${scheduledDate}T${scheduledTime}`, orgTimezone).toISOString(); } catch { /* Drafts may remain unscheduled. */ }
    }
    if (nextStatus === 'scheduled') {
      if (!selectedPlatforms.length) errors.platforms = 'Select at least one active account.';
      const dateTime = `${scheduledDate}T${scheduledTime}`;
      try {
        const date = parseFromDatetimeLocalInput(dateTime, orgTimezone);
        if (formatForDatetimeLocalInput(date, orgTimezone) !== dateTime) throw new Error('Invalid local time');
        scheduledTimeUTC = date.toISOString();
        const unchanged = editingPost?.status === 'scheduled' && Date.parse(scheduledTimeUTC) === Date.parse(editingPost.scheduled_time);
        if (!unchanged && date.getTime() <= Date.now()) errors.scheduled_time = 'Choose a future date and time.';
      } catch { errors.scheduled_time = 'Choose a valid date and time in your organization timezone.'; }
      if (accounts?.some((account: SocialMediaAccount)=>selectedPlatforms.includes(account.id) && account.platform==='instagram') && !selectedMedia.length) errors.media = 'Instagram requires an image.';
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length) { setPreviewMode(false); return; }
    // Separate media and production photos
    const marketingMedia = [];
    const productionPhotos = [];
    
    for (const media of selectedMedia) {
      const idStr = String(media.id);
      if (media.source === 'production' || idStr.startsWith('production_')) {
        // Extract numeric ID for production photos
        const productionId = parseInt(idStr.replace('production_', ''), 10);
        if (!isNaN(productionId)) {
          productionPhotos.push(productionId);
        }
      } else if (idStr.startsWith('marketing_')) {
        // Extract numeric ID for marketing media (from combined-media endpoint)
        const marketingId = parseInt(idStr.replace('marketing_', ''), 10);
        if (!isNaN(marketingId)) {
          marketingMedia.push(marketingId);
        }
      } else if (media.id) {
        // Plain numeric ID - check if it's a number
        const numericId = parseInt(idStr, 10);
        if (!isNaN(numericId)) {
          marketingMedia.push(numericId);
        }
      }
    }
    
    const postData: any = {
      title,
      content,
      post_type: postType || 'general',  // Ensure post_type is never null
      platforms: selectedPlatforms,
      scheduled_time: scheduledTimeUTC,
      status: nextStatus,
      customer_review: selectedTestimonial?.id || null,
    };
    
    // PATCH must include empty lists to remove the last attachment.
    if (editingPost && content !== editingPost.content) postData.platform_content = previewContent;
    postData.media = marketingMedia;
    postData.production_photos = productionPhotos;

    try { await createPostMutation.mutateAsync(postData); } catch { /* Inline error from mutation. */ }
  };

  const handlePlatformToggle = (platformId: number) => {
    setDirty(true);
    setSelectedPlatforms(prev => {
      if (prev.includes(platformId)) {
        return prev.filter(id => id !== platformId);
      } else {
        return [...prev, platformId];
      }
    });
  };

  const getPlatformIcon = (platform: string) => {
    switch(platform) {
      case 'facebook':
        return <FacebookIcon />;
      case 'instagram':
        return <InstagramIcon />;
      case 'google_business_profile':
        return <BusinessIcon />;
      default:
        return null;
    }
  };

  const getPostTypeIcon = (type: string) => {
    switch(type) {
      case 'testimonial':
        return <ReviewIcon />;
      case 'tip':
        return <TipsIcon />;
      case 'promotion':
        return <OfferIcon />;
      case 'project':
        return <ImageIcon />;
      case 'seasonal':
        return <CalendarIcon />;
      case 'team':
        return <BusinessIcon />;
      case 'general':
      default:
        return <AnnouncementIcon />;
    }
  };

  const isFormValid = Boolean(title.trim() && content.trim());

  const selectedPlatformDetails = accounts?.filter(
    (acc: SocialMediaAccount) => selectedPlatforms.includes(acc.id)
  ).map((acc: SocialMediaAccount) => ({
    platform: acc.platform as 'facebook' | 'instagram' | 'google_business_profile',
    id: acc.id,
    accountName: acc.account_name,
    name: acc.platform_display
  })) || [];

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <ModalDialog size="lg" sx={{ width: '90%', maxWidth: previewMode ? 1320 : 1100 }}>
          <ModalClose />
        <DialogTitle>
          {previewMode ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={() => setPreviewMode(false)}>
                <BackIcon />
              </IconButton>
              Post Preview
            </Box>
          ) : (
            editingPost ? 'Edit Social Media Post' : 'Create Social Media Post'
          )}
        </DialogTitle>
        {/* overflowX hidden: the spacing Grid inside uses negative margins,
            which otherwise produce a sliver of horizontal scroll */}
        <DialogContent onChangeCapture={()=>setDirty(true)} sx={{ overflowX: 'hidden' }}>
          {previewMode ? (
            <>
              <SocialMediaPreview
                platforms={selectedPlatformDetails}
                title={title}
                content={content}
                organizationName={organization?.name}
                organizationLogo={organization?.logo_square_full_color || organization?.logo_url}
                images={previewImages}
                platformContent={previewContent}
                scheduledTime={new Date(`${scheduledDate}T${scheduledTime}`)}
              />
              <Divider sx={{ my: 3 }} />
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => setPreviewMode(false)}
                  startDecorator={<BackIcon />}
                >
                  Back to Edit
                </Button>
                <Button
                  variant="solid"
                  color="primary"
                  startDecorator={<ScheduleIcon />}
                  onClick={() => handleSubmit()}
                  loading={createPostMutation.isPending}
                >
                  {editingPost ? 'Save Changes' : 'Schedule Post'}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Grid container spacing={3}>
            {/* Left Column - Content */}
            <Grid xs={12} md={7}>
              <Stack spacing={2}>
                {/* Post Type Selection */}
                <FormControl>
                  <FormLabel>Post Type</FormLabel>
                  <Select
                    value={postType || 'general'}
                    onChange={(_, value) => {setDirty(true);setPostType(value as string || 'general');}}
                    startDecorator={getPostTypeIcon(postType)}
                  >
                    <Option value="general">General Update</Option>
                    <Option value="testimonial">Customer Testimonial</Option>
                    <Option value="tip">Educational Tip</Option>
                    <Option value="promotion">Promotion/Offer</Option>
                    <Option value="project">Project Showcase</Option>
                    <Option value="seasonal">Seasonal Content</Option>
                    <Option value="team">Team/Culture</Option>
                  </Select>
                </FormControl>

                {/* Title */}
                <FormControl error={Boolean(fieldErrors.title)}>
                  <FormLabel>Title</FormLabel>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter post title..."
                  />
                  {fieldErrors.title && <FormHelperText>{fieldErrors.title}</FormHelperText>}
                </FormControl>

                {/* Content */}
                <FormControl error={Boolean(fieldErrors.content)}>
                  <FormLabel>Content</FormLabel>
                  <Textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Write your post content..."
                    minRows={6}
                    maxRows={12}
                  />
                  <FormHelperText>
                    {fieldErrors.content || `${content.length} characters · limits depend on the destination`}
                  </FormHelperText>
                </FormControl>

                {/* Testimonial Selection Button */}
                {postType === 'testimonial' && (
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    startDecorator={<ReviewIcon />}
                    onClick={() => setShowTestimonialSelector(true)}
                  >
                    {selectedTestimonial ? 'Change Testimonial' : 'Select Customer Testimonial'}
                  </Button>
                )}

                {/* AI Generation */}
                <Card variant="soft" color="primary">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography level="title-sm" startDecorator={<AIIcon />}>
                          AI Content Generation
                        </Typography>
                        <Typography level="body-xs">
                          {selectedMedia.length > 0
                            ? `Analyze ${selectedMedia.length > 1 ? 'images' : 'image'} and generate relevant content`
                            : 'Generate engaging content based on your post type'}
                        </Typography>
                      </Box>
                      <Button
                        size="sm"
                        loading={isGeneratingContent}
                        onClick={handleGenerateContent}
                        startDecorator={isGeneratingContent ? null : <RefreshIcon />}
                      >
                        {isGeneratingContent ? 'Generating...' : 'Generate'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>

                {generationError && (
                  <Alert
                    color="danger"
                    endDecorator={
                      <IconButton size="sm" variant="plain" onClick={() => setGenerationError(null)}>
                        <CloseIcon />
                      </IconButton>
                    }
                  >
                    {generationError}
                  </Alert>
                )}
              </Stack>
            </Grid>

            {/* Right Column - Settings */}
            <Grid xs={12} md={5}>
              <Stack spacing={2}>
                {/* Platform Selection */}
                <FormControl error={Boolean(fieldErrors.platforms)}>
                  <FormLabel>Select Platforms</FormLabel>
                  <Stack spacing={1}>
                    {accounts?.filter((acc: SocialMediaAccount) => acc.is_active).map((account: SocialMediaAccount) => (
                      <Card
                        key={account.id}
                        variant={selectedPlatforms.includes(account.id) ? 'solid' : 'outlined'}
                        color={selectedPlatforms.includes(account.id) ? 'primary' : 'neutral'}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handlePlatformToggle(account.id)}
                      >
                        <CardContent sx={{ py: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Checkbox
                              checked={selectedPlatforms.includes(account.id)}
                              onChange={() => handlePlatformToggle(account.id)}
                              // Without this, the click bubbles to the card's
                              // onClick and toggles the selection right back
                              onClick={(event) => event.stopPropagation()}
                              sx={{
                                color: selectedPlatforms.includes(account.id) ? 'common.white' : 'inherit',
                                '& .MuiCheckbox-checkbox': {
                                  backgroundColor: selectedPlatforms.includes(account.id) ? 'rgba(255,255,255,0.2)' : 'inherit',
                                  borderColor: selectedPlatforms.includes(account.id) ? 'common.white' : 'inherit',
                                }
                              }}
                            />
                            <Box sx={{ color: selectedPlatforms.includes(account.id) ? 'common.white' : 'inherit' }}>
                              {getPlatformIcon(account.platform)}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography 
                                level="title-sm"
                                sx={{ 
                                  color: selectedPlatforms.includes(account.id) ? 'common.white' : 'inherit' 
                                }}
                              >
                                {account.platform_display}
                              </Typography>
                              <Typography 
                                level="body-xs"
                                sx={{ 
                                  color: selectedPlatforms.includes(account.id) ? 'rgba(255, 255, 255, 0.8)' : 'inherit' 
                                }}
                              >
                                {account.account_name}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                  {selectedPlatforms.length === 0 && (
                    <FormHelperText>
                      {fieldErrors.platforms || 'Select accounts when you are ready to schedule'}
                    </FormHelperText>
                  )}
                </FormControl>

                {fieldErrors.platforms && selectedPlatforms.length > 0 && <Alert color="danger">{fieldErrors.platforms}</Alert>}
                {/* Schedule */}
                <Typography level="title-sm" sx={{ mb: 1 }}>Schedule · {orgTimezone}</Typography>
                {fieldErrors.scheduled_time && <Alert color="danger">{fieldErrors.scheduled_time}</Alert>}
                <Stack spacing={1}>
                  <FormControl>
                    <FormLabel>Date</FormLabel>
                    <Input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      startDecorator={<CalendarIcon />}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Time</FormLabel>
                    <Input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      startDecorator={<TimeIcon />}
                    />
                  </FormControl>
                </Stack>

                {/* Media Attachments */}
                <Card variant="outlined">
                  <CardContent>
                    <Typography level="title-sm" sx={{ mb: 1 }}>
                      Media Attachments
                    </Typography>
                    
                    {fieldErrors.media && <Alert color="danger">{fieldErrors.media}</Alert>}
                    {/* Display selected media */}
                    {selectedMedia.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Stack spacing={1}>
                          {selectedMedia.map((media) => (
                            <Card key={media.id} variant="soft" size="sm">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
                                <AspectRatio ratio="1" sx={{ width: 50, flexShrink: 0 }}>
                                  <img 
                                    src={media.thumbnail || media.file} 
                                    alt={media.name || 'Media attachment'} 
                                    style={{ objectFit: 'cover' }}
                                  />
                                </AspectRatio>
                                <Typography level="body-sm" sx={{ flex: 1, minWidth: 0 }} noWrap>
                                  {media.name || 'Untitled'}
                                </Typography>
                                <IconButton
                                  size="sm"
                                  variant="plain"
                                  color="danger"
                                  aria-label={`Remove ${media.name || 'attachment'}`}
                                  onClick={() => handleRemoveMedia(media.id)}
                                >
                                  <CloseIcon />
                                </IconButton>
                              </Box>
                            </Card>
                          ))}
                        </Stack>
                      </Box>
                    )}
                    
                    <Stack spacing={1}>
                      <Button
                        variant="soft"
                        color="primary"
                        fullWidth
                        startDecorator={<AIIcon />}
                        onClick={() => setShowImageComposer(true)}
                      >
                        Create Branded Image
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        startDecorator={<AddIcon />}
                        onClick={() => setShowMediaSelector(true)}
                      >
                        {selectedMedia.length > 0 ? 'Add More Media' : 'Add from Library'}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', flexWrap:'wrap', gap: 2, justifyContent: editingPost ? 'space-between' : 'flex-end' }}>
            {/* Delete button on the left when editing */}
            {editingPost && (
              <Button
                variant="outlined"
                color="danger"
                startDecorator={<DeleteIcon />}
                onClick={handleDelete}
                loading={deletePostMutation.isPending}
              >
                Delete Post
              </Button>
            )}
            
            {/* Regular buttons on the right */}
            <Box sx={{ display: 'flex', flexWrap:'wrap', gap: 1 }}>
              <Button
                variant="plain"
                color="neutral"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button variant="soft" onClick={() => handleSubmit('draft')} loading={createPostMutation.isPending} disabled={!isFormValid}>Save Draft</Button>
              <Button variant="soft" onClick={() => handleSubmit('pending_review')} loading={createPostMutation.isPending} disabled={!isFormValid}>Submit for Review</Button>
              <Button
                variant="outlined"
                color="primary"
                startDecorator={<PreviewIcon />}
                onClick={() => setPreviewMode(true)}
                disabled={!isFormValid}
              >
                Preview
              </Button>
              {editingPost && editingPost.status !== 'scheduled' && <Button onClick={()=>handleSubmit('scheduled')} loading={createPostMutation.isPending} disabled={!isFormValid}>Schedule Post</Button>}
              <Button
                variant="solid"
                color="primary"
                startDecorator={<ScheduleIcon />}
                onClick={() => handleSubmit()}
                loading={createPostMutation.isPending}
                disabled={!isFormValid}
              >
                {editingPost ? 'Save Changes' : 'Schedule Post'}
              </Button>
            </Box>
          </Box>
            </>
          )}
        </DialogContent>
      </ModalDialog>
    </Modal>

    <Modal open={confirmDiscard} onClose={()=>setConfirmDiscard(false)}>
      <ModalDialog role="alertdialog" aria-labelledby="discard-social-title" sx={{maxWidth:420,width:'calc(100% - 32px)'}}>
        <DialogTitle id="discard-social-title">Discard unsaved changes?</DialogTitle>
        <DialogContent>Your changes have not been saved.</DialogContent>
        <Box sx={{display:'flex',gap:1,flexWrap:'wrap',justifyContent:'flex-end'}}>
          <Button variant="plain" onClick={()=>setConfirmDiscard(false)}>Keep editing</Button>
          <Button color="danger" onClick={resetAndClose}>Discard changes</Button>
        </Box>
      </ModalDialog>
    </Modal>
    {/* Testimonial Selector Modal */}
    <TestimonialSelector
      open={showTestimonialSelector}
      onClose={() => setShowTestimonialSelector(false)}
      onSelect={handleTestimonialSelect}
      selectedReviewId={selectedTestimonial?.id}
    />

    {/* Testimonial Template Selector Modal */}
    {selectedTestimonial && (
      <TestimonialTemplateSelector
        open={showTemplateSelector}
        onClose={() => setShowTemplateSelector(false)}
        review={selectedTestimonial}
        onGenerate={handleGeneratedImage}
      />
    )}

    {/* Media Selector Modal */}
    <Modal open={showMediaSelector} onClose={() => setShowMediaSelector(false)}>
      <ModalDialog size="lg" sx={{ width: '90%', maxWidth: 1200, maxHeight: '90vh', overflow: 'hidden' }}>
        <ModalClose />
        <DialogTitle>Select Media</DialogTitle>
        <DialogContent sx={{ overflow: 'auto' }}>
          <MediaLibrary
            selectionMode={true}
            onSelect={(media) => {
              // Check if media is already selected
              if (!selectedMedia.find(m => m.id === media.id)) {
                handleMediaSelect(media);
              } else {
                setShowMediaSelector(false);
              }
            }}
            selectedMedia={null}
          />
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="plain" onClick={() => setShowMediaSelector(false)}>
              Done
            </Button>
          </Box>
        </DialogContent>
      </ModalDialog>
    </Modal>

    {/* Image Composer Modal */}
    <ImageComposer
      open={showImageComposer}
      onClose={() => setShowImageComposer(false)}
      onGenerate={(media) => {
        // Add the generated branded image to selected media
        setDirty(true);
        setSelectedMedia(prev => [...prev, media]);
        setShowImageComposer(false);
      }}
    />
    </>
  );
};